import {
  IModifier,
  IModifierMultiplier,
  ModifierType,
} from 'features/menu/components/product-detail/modifier/types';
import { QUANTITY_DEFAULTS } from 'features/menu/components/product-detail/utils';
import { MultipliersSelections } from 'features/menu/components/product-wizard/types';

import { EntryTypeEnum, IQuantity } from 'generated/graphql-gateway';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  hasOptionEntries,
  isEntryAvailable,
  mapAvailableOptions,
  stringifySanityImage,
} from 'state/unified-menu/utils';

const regExp = /\bRegular\b/gi;
// For single or multiple choice modifiers, removes the word "Regular" from the multiplier name
function guessMultiplierName(modifierType: ModifierType, name: string): string {
  if (
    modifierType !== ModifierType.SINGLE_CHOICE &&
    modifierType !== ModifierType.MULTIPLE_CHOICE
  ) {
    return name;
  }
  return name.replace(regExp, '').replace(/\s+/g, ' ').trim();
}

export function mapModifier(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  id: string,
  modifiersCount: number,
  posServiceMode: PosServiceMode,
  quantity?: IQuantity | null
): IModifier | null {
  const modifierEntry = menu.get(id, { type: EntryTypeEnum.MODIFIER_GROUP });
  if (!hasOptionEntries(posServiceMode, modifierEntry)) {
    return null;
  }

  const { min, max } = setQuantityDefaults(quantity);
  const name = modifierEntry.name?.locale ?? modifierEntry.id;
  const multipliers = mapAvailableOptions(
    menu,
    posServiceMode,
    modifierEntry.options.entries,
    option => mapModifierMultiplier(menu, modifierEntry, option.entryId, name, posServiceMode)
  );

  const baseModifier: IModifier = {
    id: modifierEntry.id,
    name,
    quantity: { min, max },
    multipliers,
    defaultMultipliers: modifierEntry.options.defaults ?? [],
    singleChoiceOnly: false,
    displayModifierMultiplierName: true,
    type: ModifierType.CHECKBOX,
    image: stringifySanityImage(modifierEntry.image),
  };

  const type = getModifierType(baseModifier);
  const singleChoiceOnly = type === ModifierType.SINGLE_CHOICE;

  const mappedMultipliers = multipliers.map(multiplier => ({
    ...multiplier,
    name: guessMultiplierName(type, multiplier.name),
  }));

  if (baseModifier.defaultMultipliers.length === 0) {
    baseModifier.defaultMultipliers = [baseModifier.multipliers[0].id];
  }

  const modifier: IModifier = {
    ...baseModifier,
    type,
    singleChoiceOnly,
    multipliers: mappedMultipliers,
  };

  return modifier;
}

export function getModifierMultiplierPrice(
  modifier: ILocalizedMenuEntry,
  multiplierId: string,
  posServiceMode: PosServiceMode
): number {
  const multiplierRef = modifier.options?.entries?.find(entry => entry.entryId === multiplierId);
  const multiplierRefPrice = multiplierRef?.price?.[posServiceMode] ?? 0;
  const modifierPrice = modifier.price?.[posServiceMode] ?? 0;
  return modifierPrice + multiplierRefPrice;
}

function mapModifierMultiplier(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  modifierEntry: ILocalizedMenuEntry,
  id: string,
  modifierName: string,
  posServiceMode: PosServiceMode
): IModifierMultiplier {
  const multiplier = menu.get(id, { type: EntryTypeEnum.MODIFIER });

  const oName = multiplier.name?.locale;
  const name = oName ?? multiplier.id;
  const nutrition = multiplier.nutrition ?? undefined;

  // TODO: figure out if prefix will arrive in API
  const prefix = guessMultiplierPrefix(oName ?? '', modifierName);

  return {
    id: multiplier.id,
    name,
    prefix,
    multiplier: guessMultiplier(multiplier.id, name), //TODO: fix once multipliers arrive in API
    nutrition,
    price: getModifierMultiplierPrice(modifierEntry, id, posServiceMode),
    image: stringifySanityImage(multiplier.image),
  };
}

/**
 * Keeps disabled modifiers visible and removes enabled modifiers that cannot be modified.
 * Example: enabled modifiers with only one option available where the user must select one.
 */
export function getValidModifiersForSelection(modifiers: IModifier[]): IModifier[] {
  return modifiers.filter(modifier => {
    if (modifier.disabled) {
      return true;
    }

    const requiresUserSelection =
      modifier.type === ModifierType.MULTIPLE_CHOICE ||
      modifier.type === ModifierType.SINGLE_CHOICE;
    if (requiresUserSelection && modifier.multipliers.length <= 1) {
      // nothing for the user to select if there is 0 or 1 option
      return false;
    }

    return true;
  });
}

export function getMultiplierLabel(
  allOptionsHavePrefix: boolean,
  selectedMultiplier: IModifierMultiplier
): string {
  return allOptionsHavePrefix && selectedMultiplier.prefix
    ? selectedMultiplier.prefix
    : selectedMultiplier.multiplier.toString();
}

/**
 * Gets the selected multiplier for a modifier.
 * It will return the first selected multiplier if there are multiple selected.
 * If there are none selected, it will return the default multiplier.
 */
export function getSelectedMultiplier(
  modifier: IModifier,
  selections: MultipliersSelections
): IModifierMultiplier {
  const selectedMultiplierId = Object.keys(selections)[0] ?? modifier.defaultMultipliers[0];
  return modifier.multipliers.find(multiplier => multiplier.id === selectedMultiplierId)!;
}

/**
 * Identifies if a multiplier is visually selected.
 * While a multiplier can be selected, it might not be visually selected in the UI.
 * For example, a default of No Cheese will not be visually selected.
 * @param modifier
 * @param multiplier
 * @returns
 */
export function isMultiplierVisuallySelected(multiplier: IModifierMultiplier) {
  const isZeroMultiplier = multiplier.multiplier === 0;
  return !isZeroMultiplier;
}

export const logProductModifierCustomization = (
  text: string,
  headerText: string,
  componentKey: string,
  quantity?: number
) => {
  logButtonClick({
    attributes: {
      component: ClickEventComponentNames.PRODUCT_MODIFIER_CUSTOMIZATION,
      componentId: componentKey,
      quantity,
      headerText,
      name: text,
    },
  });
};

function getModifierType(modifier: IModifier): ModifierType {
  if (isMultiChoiceModifier(modifier)) {
    return ModifierType.MULTIPLE_CHOICE;
  } else if (isCheckboxModifier(modifier)) {
    return ModifierType.CHECKBOX;
  } else if (isSingleChoiceModifier(modifier)) {
    return ModifierType.SINGLE_CHOICE;
  } else if (isNumericModifier(modifier)) {
    return ModifierType.NUMERIC;
  } else if (isNonNumericModifier(modifier)) {
    return ModifierType.NON_NUMERIC;
  }
  return ModifierType.NON_NUMERIC;
}

function isCheckboxModifier(modifier: IModifier): boolean {
  if (modifier.quantity.max !== 1) {
    return false;
  }

  if (modifier.multipliers.length !== 2) {
    return false;
  }
  return modifier.multipliers.every(
    multiplier => multiplier.multiplier === 0 || multiplier.multiplier === 1
  );
}

function isNonNumericModifier(modifier: IModifier): boolean {
  return !modifier.singleChoiceOnly && modifier.displayModifierMultiplierName;
}

function isNumericModifier(modifier: IModifier): boolean {
  return !modifier.singleChoiceOnly && !modifier.displayModifierMultiplierName;
}

function isSingleChoiceModifier(modifier: IModifier): boolean {
  const multipliers = modifier.multipliers.map(modififer => modififer.multiplier);
  const hasOnlyBinaryValues = multipliers.every(multiplier => multiplier === 0 || multiplier === 1);

  return modifier.quantity.max === 1 && hasOnlyBinaryValues;
}

function isMultiChoiceModifier(modifier: IModifier): boolean {
  return modifier.quantity.max > 1;
}

function guessMultiplier(id: string, name: string) {
  if (id.includes('_0-00')) {
    return 0;
  }
  if (id.includes('_0-50')) {
    return 0.5;
  }
  if (id.includes('_1-00')) {
    return 1;
  }
  if (id.includes('_1-50')) {
    return 1.5;
  }
  if (id.includes('_2-00')) {
    return 2;
  }

  name = name.toLowerCase();
  if (name.includes('light')) {
    return 0.5;
  }
  if (name.includes('regular')) {
    return 1;
  }
  if (name.includes('extra')) {
    return 2; // could be 1.5 or 2. Just guessing
  }
  if (name.includes('dipping sauce')) {
    return 1;
  }
  return 0;
}

function guessMultiplierPrefix(multiplierName: string, modifierName: string): string {
  const [firstWord] = multiplierName.split(' ');
  if (['No', 'Light', 'Regular', 'Extra'].includes(firstWord)) {
    return firstWord;
  }

  const regex = new RegExp(modifierName, 'i');
  return multiplierName.replace(regex, '').trim();
}

function setQuantityDefaults(quantity: IQuantity | undefined | null): IModifier['quantity'] {
  return {
    min: quantity?.min ?? QUANTITY_DEFAULTS.min,
    max: quantity?.max ?? QUANTITY_DEFAULTS.max,
  };
}

type ModifierMultiplierWithAvailability = IModifierMultiplier & { available: boolean };

function getModifierActionFlags(
  multipliers: ModifierMultiplierWithAvailability[],
  defaultMultiplierIds: readonly string[]
): { shouldSkipModifier: boolean; shouldDisableModifier: boolean } {
  // If there is no "No" (0) option within the modifer, the default is an included customization (> 0),
  // and every multiplier is unavailable, skip the modifier.
  const modifierHasZeroMultiplier = multipliers.some(multiplier => multiplier.multiplier === 0);
  const defaultMultipliers = multipliers.filter(multiplier =>
    defaultMultiplierIds.includes(multiplier.id)
  );

  const hasIncludedDefault = defaultMultipliers.some(multiplier => multiplier.multiplier > 0);
  const allMultipliersUnavailable = multipliers.every(multiplier => !multiplier.available);

  if (!modifierHasZeroMultiplier && hasIncludedDefault && allMultipliersUnavailable) {
    return { shouldSkipModifier: true, shouldDisableModifier: false };
  }

  let shouldSkipModifier = false;
  let shouldDisableModifier = false;

  for (const multiplier of multipliers) {
    const isDefaultMultiplier = defaultMultiplierIds.includes(multiplier.id);

    // If the default multiplier is not available and it's not part of the included customizations, skip the modifier
    if (isDefaultMultiplier && multiplier.multiplier === 0 && !multiplier.available) {
      shouldSkipModifier = true;
      break;
    }

    // If the default multiplier is not available and it's part of the included customization, then disable the modifier
    if (isDefaultMultiplier && multiplier.multiplier > 0 && !multiplier.available) {
      shouldDisableModifier = true;
    }
  }

  return { shouldSkipModifier, shouldDisableModifier };
}

export function mapModifierForCustomization(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  id: string,
  posServiceMode: PosServiceMode,
  quantity?: IQuantity | null
): IModifier | null {
  const modifierEntry = menu.get(id, {
    type: EntryTypeEnum.MODIFIER_GROUP,
  });

  // Do not use hasOptionEntries here: unavailable default multipliers must still be mapped so
  // getModifierActionFlags can determine whether the modifier should be skipped or disabled.
  const optionEntries = modifierEntry.options?.entries;

  if (!optionEntries?.length) {
    return null;
  }

  const { min, max } = setQuantityDefaults(quantity);

  const name = modifierEntry.name?.locale ?? modifierEntry.id;

  const defaultMultipliers = modifierEntry.options?.defaults ?? [];

  const multipliers = optionEntries.reduce<Array<IModifierMultiplier & { available: boolean }>>(
    (acc, option) => {
      const isDefaultMultiplier = defaultMultipliers.includes(option.entryId);
      const available = isEntryAvailable(option, posServiceMode, undefined);

      const mutiplierExistisInMenu = !!menu.find(option.entryId, { type: EntryTypeEnum.MODIFIER });

      // This is a very edge case scenario, so if we don't have the multiplier in the menu, we skip the modifier
      if (!mutiplierExistisInMenu) {
        return acc;
      }

      // Skip unavailable non-default multipliers
      if (!isDefaultMultiplier && !available) {
        return acc;
      }

      const mappedMultiplier = mapModifierMultiplier(
        menu,
        modifierEntry,
        option.entryId,
        name,
        posServiceMode
      );

      acc.push({
        ...mappedMultiplier,
        available,
      });

      return acc;
    },
    []
  );

  // All options may have been filtered out because they are missing from the menu or are
  // unavailable non-defaults. In that case, there is no modifier content to render.
  if (multipliers.length === 0) {
    return null;
  }

  const { shouldSkipModifier, shouldDisableModifier } = getModifierActionFlags(
    multipliers,
    defaultMultipliers
  );

  if (shouldSkipModifier) {
    return null;
  }

  const baseModifier: IModifier = {
    id: modifierEntry.id,
    name,
    quantity: { min, max },
    multipliers,
    defaultMultipliers,
    singleChoiceOnly: false,
    displayModifierMultiplierName: true,
    type: ModifierType.CHECKBOX,
    image: stringifySanityImage(modifierEntry.image),
    disabled: shouldDisableModifier,
  };

  const type = getModifierType(baseModifier);

  return {
    ...baseModifier,
    type,
    singleChoiceOnly: type === ModifierType.SINGLE_CHOICE,
    multipliers: multipliers.map(({ available, ...multiplier }) => ({
      ...multiplier,
      name: guessMultiplierName(type, multiplier.name),
    })),
  };
}

export const mapMultiplierName = (value: number): string => {
  if (value < 0) {
    return '';
  }
  if (value === 0) {
    return 'No';
  }
  if (value === 0.5) {
    return 'Light';
  }
  if (value === 1) {
    return 'Regular';
  }
  if (value > 1) {
    return 'Extra';
  }

  return '';
};
